"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useAuth } from './auth-context';

interface AdminNotificationItem {
  id: string;
  type: string;
  title: string;
  message: string;
  createdAt: string;
  data: {
    itemId: string;
    tab: string;
    [key: string]: any;
  };
}

interface AdminNotificationContextType {
  pendingItems: AdminNotificationItem[];
  counts: {
    total: number;
    tutorApplications: number;
    certRequests: number;
    advertiserApplications: number;
    adCampaigns: number;
    appeals: number;
    contentReports: number;
    supportTickets: number;
    jobApplications: number;
  };
  refreshNotifications: () => void;
  isLoading: boolean;
}

const AdminNotificationContext = createContext<AdminNotificationContextType | undefined>(undefined);

interface AdminNotificationProviderProps {
  children: ReactNode;
}

export function AdminNotificationProvider({ children }: AdminNotificationProviderProps) {
  const [pendingItems, setPendingItems] = useState<AdminNotificationItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { user, isAuthenticated } = useAuth();

  // Fetch pending items from API
  const fetchPendingItems = useCallback(async () => {
    if (!isAuthenticated || !user || (user.role !== 'ADMIN' && !user.roles?.includes('admin'))) {
      setPendingItems([]);
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch('/api/admin/pending-notifications');
      const data = await res.json();
      setPendingItems(data.notifications || []);
    } catch (error) {
      console.error('Failed to fetch admin notifications:', error);
      setPendingItems([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  // Calculate counts
  const counts = {
    total: pendingItems.length,
    tutorApplications: pendingItems.filter(item => item.type === 'tutor_application').length,
    certRequests: pendingItems.filter(item => item.type === 'certification_request').length,
    advertiserApplications: pendingItems.filter(item => item.type === 'advertiser_application').length,
    adCampaigns: pendingItems.filter(item => item.type === 'ad_campaign').length,
    appeals: pendingItems.filter(item => item.type === 'appeal').length,
    contentReports: pendingItems.filter(item => item.type === 'content_report').length,
    supportTickets: pendingItems.filter(item => item.type === 'support_ticket').length,
    jobApplications: pendingItems.filter(item => item.type === 'job_application').length,
  };

  const refreshNotifications = () => {
    fetchPendingItems();
  };

  // Initial load when user is authenticated and is admin
  useEffect(() => {
    if (isAuthenticated && user && (user.role === 'ADMIN' || user.roles?.includes('admin'))) {
      fetchPendingItems();
    } else {
      // Clear notifications when user logs out or is not admin
      setPendingItems([]);
    }
  }, [isAuthenticated, user, fetchPendingItems]);

  // Poll for new notifications every 30 seconds during authenticated admin session
  useEffect(() => {
    if (!isAuthenticated || !user || (user.role !== 'ADMIN' && !user.roles?.includes('admin'))) {
      return;
    }

    const interval = setInterval(fetchPendingItems, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated, user, fetchPendingItems]);

  return (
    <AdminNotificationContext.Provider
      value={{
        pendingItems,
        counts,
        refreshNotifications,
        isLoading,
      }}
    >
      {children}
    </AdminNotificationContext.Provider>
  );
}

export function useAdminNotifications(): AdminNotificationContextType {
  const context = useContext(AdminNotificationContext);
  if (context === undefined) {
    throw new Error('useAdminNotifications must be used within an AdminNotificationProvider');
  }
  return context;
} 